import { create } from "zustand";

/**
 * User-scoped store for the tiers the effective timezone resolves from.
 *
 * This replaces the module-level `let` cache that `getTimezone()` used to read.
 * The difference that matters: a zustand store is written from committed
 * effects and read via `getState()`, so non-hook consumers always observe state
 * that belongs to a render React actually kept. The old globals were mutated
 * during render, which meant an abandoned concurrent render — an interrupted
 * update, a discarded transition — could leave *its* tenant's or location's zone
 * behind with nothing to put it back, and every `getTimezone()`-based request
 * would carry the wrong day boundaries until something else committed.
 *
 * `timezoneOverride` and the selected location belong to the signed-in user:
 * `setTimezoneUser` keys them to a firebase uid and clears them whenever it
 * changes, so one user's resolved zone can never be observed by the next. The
 * tenant tiers belong to the tenant and are cleared when TenantProvider
 * unmounts.
 *
 * Deliberately NOT persisted. Every tier is derived from server state that is
 * refetched on load; a rehydrated zone would just be a stale one.
 *
 * TRADEOFF, stated plainly: because the providers write from effects and a
 * parent's effect runs after its children's, a non-hook `getTimezone()` call
 * made from a child effect in the same commit as a location switch reads the
 * previous zone. That staleness is bounded by one commit and self-heals. The
 * render-phase write it replaces had no such bound — a discarded render could
 * strand the wrong zone until something unrelated committed. Hook consumers are
 * unaffected either way: `useTimezone()` resolves from context during render.
 */
interface TimezoneStore {
  /** Firebase uid these tiers belong to; tiers reset when it changes. */
  firebaseUid: string | null;
  /** The user's explicit display-timezone preference. */
  timezoneOverride: string | null;
  /** Timezone of the user's currently selected location. */
  currentLocationTimezone: string | null;
  /**
   * Whether a location is selected at all. One with no usable timezone of its
   * own falls back to the tenant default even when locations span zones.
   */
  hasCurrentLocation: boolean;
  /** The tenant's configured default timezone. */
  tenantDefaultTimezone: string | null;
  /**
   * Whether the tenant's active locations span more than one timezone. The
   * default then names one location's clock, not the clinic's, so the
   * resolver skips it.
   */
  tenantSpansTimezones: boolean;

  setTimezoneUser: (firebaseUid: string | null) => void;
  setTimezoneOverride: (tz: string | null) => void;
  setCurrentLocation: (tz: string | null, selected: boolean) => void;
  setTenantDefaultTimezone: (tz: string | null) => void;
  setTenantSpansTimezones: (spans: boolean) => void;
}

// The tiers that belong to the signed-in user. The tenant tiers are written by
// providers that don't re-run on a user change, so clearing them here would
// leave them empty until the tenant itself changed.
const EMPTY_USER_TIERS = {
  timezoneOverride: null,
  currentLocationTimezone: null,
  hasCurrentLocation: false,
} as const;

export const useTimezoneStore = create<TimezoneStore>((set) => ({
  firebaseUid: null,
  ...EMPTY_USER_TIERS,
  tenantDefaultTimezone: null,
  tenantSpansTimezones: false,

  setTimezoneUser: (firebaseUid) =>
    set((state) =>
      state.firebaseUid === firebaseUid
        ? state
        : { firebaseUid, ...EMPTY_USER_TIERS },
    ),

  // Each setter no-ops on an unchanged value so a re-assert of the same tier
  // doesn't notify subscribers.
  setTimezoneOverride: (tz) =>
    set((state) =>
      state.timezoneOverride === tz ? state : { timezoneOverride: tz },
    ),
  setCurrentLocation: (tz, selected) =>
    set((state) =>
      state.currentLocationTimezone === tz &&
      state.hasCurrentLocation === selected
        ? state
        : { currentLocationTimezone: tz, hasCurrentLocation: selected },
    ),
  setTenantDefaultTimezone: (tz) =>
    set((state) =>
      state.tenantDefaultTimezone === tz
        ? state
        : { tenantDefaultTimezone: tz },
    ),
  setTenantSpansTimezones: (spans) =>
    set((state) =>
      state.tenantSpansTimezones === spans
        ? state
        : { tenantSpansTimezones: spans },
    ),
}));
